// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Checkbox, Group, Input, Select, Stack, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { isEmail, useForm } from '@mantine/form';
import type { Questionnaire, QuestionnaireItem, QuestionnaireResponse } from '@medplum/fhirtypes';
import { buildQuestionnaireResponse, isUsPhone10, normalizeUsPhoneToE164 } from '@polarissleep/shared';
import dayjs from 'dayjs';
import type { JSX } from 'react';
import { IMaskInput } from 'react-imask';

interface PatientInfoFormProps {
  questionnaire: Questionnaire;
  onSubmit: (response: QuestionnaireResponse) => void;
  onBack?: () => void;
  submitting?: boolean;
}

interface PatientInfoFormValues {
  'first-name': string;
  'last-name': string;
  email: string;
  phone: string;
  'sex-at-birth': string;
  'gender-identity': string;
  'gender-identity-other': string;
  'self-pay': boolean;
  'insurance-provider': string;
  'member-id': string;
  'date-of-birth': Date | null;
  'treatment-reason': string;
  'other-reason': string;
}

const MEMBER_ID_REGEX = /^[A-Za-z0-9-]{5,30}$/;
const OTHER_REASON_OPTION = 'Other (please specify)';

function getStringOptions(item?: QuestionnaireItem): string[] {
  return (item?.answerOption ?? []).map((opt) => opt.valueString).filter((s): s is string => typeof s === 'string');
}

function getCodingOptions(item?: QuestionnaireItem): { value: string; label: string }[] {
  return (item?.answerOption ?? [])
    .filter((opt) => opt.valueCoding)
    .map((opt) => ({
      value: opt.valueCoding?.code ?? '',
      label: opt.valueCoding?.display ?? opt.valueCoding?.code ?? '',
    }));
}

function getItem(questionnaire: Questionnaire, linkId: string): QuestionnaireItem | undefined {
  return questionnaire.item?.find((i) => i.linkId === linkId);
}

export function PatientInfoForm({ questionnaire, onSubmit, onBack, submitting }: PatientInfoFormProps): JSX.Element {
  const insuranceOptions = getStringOptions(getItem(questionnaire, 'insurance-provider'));
  const sexOptions = getCodingOptions(getItem(questionnaire, 'sex-at-birth'));
  const genderOptions = getCodingOptions(getItem(questionnaire, 'gender-identity'));
  const reasonOptions = getStringOptions(getItem(questionnaire, 'treatment-reason'));

  const form = useForm<PatientInfoFormValues>({
    initialValues: {
      'first-name': '',
      'last-name': '',
      email: '',
      phone: '',
      'sex-at-birth': '',
      'gender-identity': '',
      'gender-identity-other': '',
      'self-pay': false,
      'insurance-provider': '',
      'member-id': '',
      'date-of-birth': null,
      'treatment-reason': '',
      'other-reason': '',
    },
    validate: {
      'first-name': (v) => (v.trim() ? null : 'First name is required'),
      'last-name': (v) => (v.trim() ? null : 'Last name is required'),
      email: isEmail('Enter a valid email address'),
      phone: (v) => (isUsPhone10(v) ? null : 'Enter a valid 10-digit US phone number'),
      'sex-at-birth': (v) => (v ? null : 'Sex at birth is required'),
      'insurance-provider': (v, values) => (values['self-pay'] || v ? null : 'Insurance provider is required'),
      'member-id': (v, values) => {
        if (values['self-pay']) {
          return null;
        }
        if (!v.trim()) {
          return 'Member ID is required';
        }
        if (!MEMBER_ID_REGEX.test(v.trim())) {
          return 'Member ID must be 5–30 letters, digits, or dashes';
        }
        return null;
      },
      'date-of-birth': (v) => (v ? null : 'Date of birth is required'),
      'gender-identity-other': (v, values) =>
        values['gender-identity'] === 'other' && !v.trim() ? 'Please specify your gender identity' : null,
      'treatment-reason': (v) => (v ? null : 'Treatment reason is required'),
      'other-reason': (v, values) =>
        values['treatment-reason'] === OTHER_REASON_OPTION && !v.trim() ? 'Please specify the other reason' : null,
    },
  });

  const handleSubmit = form.onSubmit((values) => {
    const qrValues: Record<string, unknown> = {
      'first-name': values['first-name'].trim(),
      'last-name': values['last-name'].trim(),
      email: values.email.trim(),
      phone: normalizeUsPhoneToE164(values.phone) ?? values.phone,
      'sex-at-birth': values['sex-at-birth'],
      'self-pay': values['self-pay'],
      'date-of-birth': values['date-of-birth'] ? dayjs(values['date-of-birth']).format('YYYY-MM-DD') : undefined,
      'treatment-reason': values['treatment-reason'],
    };
    if (values['gender-identity']) {
      qrValues['gender-identity'] = values['gender-identity'];
    }
    if (values['gender-identity'] === 'other' && values['gender-identity-other'].trim()) {
      qrValues['gender-identity-other'] = values['gender-identity-other'].trim();
    }
    if (!values['self-pay']) {
      qrValues['insurance-provider'] = values['insurance-provider'];
      qrValues['member-id'] = values['member-id'].trim();
    }
    if (values['treatment-reason'] === OTHER_REASON_OPTION) {
      qrValues['other-reason'] = values['other-reason'].trim();
    }
    onSubmit(buildQuestionnaireResponse(questionnaire, qrValues));
  });

  const isOther = form.values['treatment-reason'] === OTHER_REASON_OPTION;
  const showGenderOther = form.values['gender-identity'] === 'other';
  const showInsurance = !form.values['self-pay'];

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Stack gap="sm">
        <Group grow>
          <TextInput label="First Name" withAsterisk {...form.getInputProps('first-name')} />
          <TextInput label="Last Name" withAsterisk {...form.getInputProps('last-name')} />
        </Group>
        <Group grow>
          <TextInput
            label="Email address"
            inputMode="email"
            autoComplete="email"
            placeholder="patient@example.com"
            withAsterisk
            {...form.getInputProps('email')}
          />
          <Input.Wrapper label="Phone number" withAsterisk {...form.getInputProps('phone')}>
            <Input
              component={IMaskInput}
              mask="(000) 000-0000"
              placeholder="(555) 555-5555"
              {...form.getInputProps('phone')}
            />
          </Input.Wrapper>
        </Group>
        <Group grow>
          <Select
            label="Sex at Birth"
            data={sexOptions}
            withAsterisk
            searchable={false}
            {...form.getInputProps('sex-at-birth')}
          />
          <Select
            label="Gender Identity"
            data={genderOptions}
            searchable={false}
            clearable
            {...form.getInputProps('gender-identity')}
          />
        </Group>
        {showGenderOther && (
          <TextInput label="Please specify" withAsterisk {...form.getInputProps('gender-identity-other')} />
        )}
        <DatePickerInput
          label="Date of Birth"
          placeholder="MM/DD/YYYY"
          maxDate={new Date()}
          valueFormat="MM/DD/YYYY"
          withAsterisk
          {...form.getInputProps('date-of-birth')}
        />
        <Checkbox label="I would like to pay out of pocket" {...form.getInputProps('self-pay', { type: 'checkbox' })} />
        {showInsurance && (
          <Group grow>
            <Select
              label="Insurance provider"
              data={insuranceOptions}
              withAsterisk
              {...form.getInputProps('insurance-provider')}
            />
            <TextInput label="Member ID" withAsterisk {...form.getInputProps('member-id')} />
          </Group>
        )}
        <Select
          label="Reason for seeking treatment"
          data={reasonOptions}
          withAsterisk
          {...form.getInputProps('treatment-reason')}
        />
        {isOther && (
          <TextInput
            label="Please specify"
            placeholder="Describe the reason"
            withAsterisk
            {...form.getInputProps('other-reason')}
          />
        )}
        <Group justify="flex-end" mt="md">
          {onBack && (
            <Button variant="subtle" color="blue" onClick={onBack} disabled={submitting} type="button">
              Back
            </Button>
          )}
          <Button type="submit" loading={submitting} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Continue'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
